/**
 * CALM DON — Lesefortschritt als Balken am oberen Rand.
 *
 * Der Entwurf hat ihn auf **vierzehn von fünfzehn** Seiten, überall mit
 * derselben Regel (nur die 404 kennt ihn nicht). Er gehört deshalb in den
 * globalen Rahmen und nicht in eine Section.
 *
 * ⚠️ **Warum das nicht der Schnipsel des Entwurfs sein kann.**
 * Dort steht sinngemäss:
 *
 *     var m = document.documentElement.scrollHeight - innerHeight;
 *     b.style.transform = 'scaleX(' + (scrollY / m) + ')';
 *     addEventListener('scroll', …);
 *
 * Drei Dinge daran sind in Horizon falsch, und alle drei still:
 *
 *   1. `window.scrollY` ist am Desktop **konstant 0**. Ab 990 px legt Horizon
 *      ein „Squeeze"-Layout an: `html` und `body` stehen auf
 *      `overflow: hidden`, gescrollt wird in `.page-wrapper`
 *      (`assets/base.css:35-41`).
 *   2. Scroll-Ereignisse eines Elements **bubbeln nicht** zum Fenster. Ein
 *      Zuhörer auf `window` feuert dort nie.
 *   3. `documentElement.scrollHeight` ist dann nicht die Länge des Inhalts.
 *
 * Am Handy fiele nichts davon auf — dort scrollt das Dokument. Der Balken
 * stünde also auf dem Telefon richtig und am Rechner für immer auf null.
 * Genau diese Falle hat schon das Scrollytelling gekostet: Es pinnte, wechselte
 * aber nie die Station.
 *
 * Deshalb kommt hier alles aus `@theme/scroll-container`.
 *
 * ⚠️ **Und der Behälter wechselt zur Laufzeit.** Die 990-px-Grenze ist eine
 * Media Query; wer das Fenster darüber hinweg zieht, bekommt einen anderen
 * Scroll-Behälter. Der Zuhörer muss dann umgehängt werden — sonst hört er
 * weiter am alten Element, das nicht mehr scrollt.
 *
 * Das Element wird hier erzeugt statt in Liquid gerendert: `render 'cd-chrome'`
 * steht im `<head>` (`layout/theme.liquid:56`), dort kann kein `div` stehen,
 * und ein zweiter Render-Aufruf im `<body>` wäre ein sechster Eingriff in eine
 * Horizon-Kerndatei. Der Balken ist reine Dekoration mit `aria-hidden` — bleibt
 * er bei einem Modulfehler aus, ist das das richtige Fehlerbild. (Anders als
 * bei `cd-motion.js`, wo ein Fehlschlag Inhalt verstecken würde; dort gibt es
 * deshalb die Rückholung über `__cdMotionReady`.)
 */

import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

const BAR_CLASS = 'cd-progress';

/** @type {HTMLElement | null} */
let bar = null;
/** @type {EventTarget | null} */
let boundTarget = null;
let ticking = false;
/** @type {ResizeObserver | null} */
let resizeObserver = null;
/** @type {Element | null} */
let observedContainer = null;

/**
 * Rechnet den Fortschritt aus dem Behälter, der gerade wirklich scrollt.
 *
 * @returns {number} 0 bis 1
 */
function progress() {
  const container = getScrollContainer();
  if (!container) return 0;

  // `scrollHeight - clientHeight` ist die tatsächlich scrollbare Strecke.
  // Bei kurzen Seiten ist sie 0 — dann bleibt der Balken leer statt durch eine
  // Division durch null auf `NaN` zu laufen und die Regel zu verwerfen.
  const scrollable = container.scrollHeight - container.clientHeight;
  if (scrollable <= 0) return 0;

  return Math.min(1, Math.max(0, container.scrollTop / scrollable));
}

function paint() {
  ticking = false;
  if (!bar) return;
  bar.style.setProperty('--cd-progress', String(progress()));
}

function request() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(paint);
}

/**
 * Hängt den Scroll-Zuhörer an den aktuell gültigen Behälter — und vom alten ab.
 */
function bind() {
  const target = getScrollEventTarget();
  if (target === boundTarget) return;

  if (boundTarget) boundTarget.removeEventListener('scroll', request);
  target.addEventListener('scroll', request, { passive: true });
  boundTarget = target;
  request();
}

/**
 * Beobachtet die Höhe des aktuellen Behälters.
 *
 * ⚠️ Muss beim Behälterwechsel mitwandern: Ein Beobachter, der beim ersten
 * Aufruf an `.page-wrapper` gehängt wurde, meldet nach dem Wechsel auf das
 * Dokument nichts mehr.
 */
function observe() {
  if (!('ResizeObserver' in window)) return;
  const container = getScrollContainer();
  if (!container || container === observedContainer) return;

  resizeObserver?.disconnect();
  resizeObserver = new ResizeObserver(request);
  resizeObserver.observe(container);
  observedContainer = container;
}

function init() {
  // Der Entwurf blendet den Balken bei `prefers-reduced-motion` aus. Das steht
  // zusätzlich im CSS; hier spart es den Beobachter gleich mit.
  if (prefersReducedMotion()) return;
  if (document.querySelector(`.${BAR_CLASS}`)) return;

  bar = document.createElement('div');
  bar.className = BAR_CLASS;
  bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar);

  bind();
  window.addEventListener('resize', request, { passive: true });

  // ⚠️ Der Behälterwechsel an der 990-px-Grenze. `scrollContainerMediaQuery`
  // ist dieselbe Query, die `scroll-container.js` intern benutzt — damit kann
  // die Grenze nicht auseinanderlaufen.
  scrollContainerMediaQuery.addEventListener('change', () => {
    // ⚠️ Hier NICHT `boundTarget = null` setzen. `bind()` braucht die alte
    // Referenz, um den Zuhörer dort abzuhängen; ohne sie bliebe er für immer
    // am alten Behälter kleben und liefe bei jedem Wechsel einer mehr mit.
    bind();
    observe();
  });

  // Die Seitenlänge ändert sich, ohne dass jemand scrollt oder das Fenster
  // zieht: Bilder laden nach, Akkordeons klappen auf, der Theme-Editor tauscht
  // eine Section aus. Ohne das steht der Balken auf einem veralteten Nenner.
  observe();

  document.addEventListener('shopify:section:load', request);
  document.addEventListener('shopify:section:unload', request);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
